import FormControl from "common/components/FormControl/FormControl";
import FormScrollError from "common/components/FormScrollError/FormScrollError";
import { useCallback, useMemo, useState } from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import arrayMutators from "final-form-arrays";
import EditHeader from "common/components/EditHeader/EditHeader";
import useRouter from "common/hooks/use-router";
import HeaderButtonsEdit from "common/components/EditHeader/HeaderButtonsEdit";
import { FieldArray } from "react-final-form-arrays";
import SortableList from "common/components/SortableList/SortableList";
import FormAddButton from "common/components/FormAddButton.tsx/FormAddButton";
import { BiMoveVertical } from "react-icons/bi";
import Button from "common/components/Button/Button";
import ToggleSwitch from "common/components/ToggleSwitch/ToggleSwitch";
import usePark from "common/hooks/usePark";
import Icon from "components/Icon/Icon";
import useAuth from "common/hooks/useAuth";

function DragHandle(props: any) {
  return (
    <BiMoveVertical
      {...props}
      className="w-4 h-4 cursor-move hover:text-primary border-none"
    />
  );
}

function QuestionItem(form: any, values: any, rest: any, isEnabled: boolean) {
  const { t } = useTranslation();

  return (
    <div
      key={rest.name}
      className="relative mt-2 flex gap-3 text-sm items-start justify-between w-full rounded-lg border border-emerald-300 py-2 pl-3 pr-1 bg-emerald-100"
    >
      {isEnabled && (
        <div
          className="min-h-[3rem] w-12 p-0 flex justify-center items-center cursor-pointer hover:text-primary bg-white rounded-sm border-slate-200"
          {...rest.dragProps}
        >
          <DragHandle />
        </div>
      )}
      <div className="flex flex-col w-full gap-3 py-2">
        <div className="flex flex-row gap-2 items-start">
          <FormControl
            name={`${rest.name}.key`}
            title={t("forms.questionKey")}
            containerStyle="w-20 shrink-0"
            required
            editable={isEnabled}
            className="bg-slate-200 w-full text-black"
          />
          <FormControl
            name={`${rest.name}.title`}
            title={t("forms.questionTitle")}
            containerStyle="flex-1 min-w-0"
            required
            type="textarea"
            editable={isEnabled}
            className="bg-slate-200 w-full text-black"
          />
          <FormControl
            name={`${rest.name}.note`}
            type="textarea"
            title={t("forms.questionNote")}
            className="bg-slate-200 w-full text-black h-10"
            containerStyle="w-1/3 min-w-0"
            editable={isEnabled}
          />
          <FormControl
            name={`${rest.name}.noteImage`}
            type="image"
            title={t("forms.questionNoteImage")}
            labelIcon="PhotoIcon"
            keepRatio={false}
            editable={isEnabled}
            disabled={!isEnabled}
            containerStyle="shrink-0"
            className="w-fit border border-slate-200 rounded-lg overflow-hidden"
          />
        </div>
        <div className="mt-2 gap-2 text-slate-700">
          <FormControl
            type="custom"
            name={`${rest.name}.isImportant`}
            editable={isEnabled}
          >
            <ToggleSwitch
              rightLabel={t("forms.isQuestionImportant")}
              isDisabled={!isEnabled}
            />
          </FormControl>
        </div>
      </div>

      <div className="">
        <Button
          type="error-line"
          onClick={() => rest.fields.remove(rest.index)}
          icon="TrashIcon"
          className="min-h-[3rem] w-12 p-0 flex justify-center"
          compact
          disabled={!isEnabled}
        />
      </div>
    </div>
  );
}

function SectionItem(form: any, values: any, rest: any, isEnabled: boolean) {
  const { t } = useTranslation();

  return (
    <div
      key={rest.name}
      className="relative mt-2 flex gap-3 text-sm items-start justify-between w-full rounded-lg border border-green-900/10 py-2 pl-3 pr-1 bg-green-900/5"
    >
      <div
        className="min-h-[3rem] w-12 p-0 flex justify-center items-center cursor-pointer hover:text-primary bg-white rounded-sm border-slate-200"
        {...rest.dragProps}
      >
        <DragHandle />
      </div>

      <div className="flex flex-col w-full gap-3">
        <div className="flex flex-row w-full gap-2">
          <div className="text-xs w-20">
            <FormControl
              name={`${rest.name}.titleKey`}
              title={t("forms.questionKey")}
              containerStyle="w-full"
              required
              editable={isEnabled}
            />
          </div>
          <div className="text-xs w-full">
            <FormControl
              name={`${rest.name}.title`}
              title={t("forms.sectionTitle")}
              containerStyle="w-full"
              required
              editable={isEnabled}
            />
          </div>
          <div className="text-xs w-full">
            <FormControl
              name={`${rest.name}.groupTitle`}
              title={t("forms.sectionGroupTitle")}
              containerStyle="w-full"
              editable={isEnabled}
            />
          </div>
        </div>
        <div className="text-xs gap-2">
          <p className="text-sm gap-1 text-slate-500 font-bold mb-2">
            {t("forms.questions")}
          </p>
          <div className="flex flex-col gap-2">
            <FieldArray name={`${rest.name}.questions`}>
              {({ fields: f }: any) => (
                <div>
                  <SortableList
                    items={f}
                    onMove={f.move}
                    render={(rest2) =>
                      QuestionItem(form, values, rest2, isEnabled)
                    }
                  />

                  {isEnabled && (
                    <FormAddButton
                      label={t("newItem.question")}
                      onClick={() =>
                        f.push({
                          id: Math.floor(Math.random() * 10000),
                        })
                      }
                    />
                  )}
                </div>
              )}
            </FieldArray>
          </div>
        </div>
      </div>

      <div className="">
        <Button
          type="error-line"
          onClick={() => rest.fields.remove(rest.index)}
          icon="TrashIcon"
          className="min-h-[3rem] w-12 p-0 flex justify-center"
          compact
          disabled={!isEnabled}
        />
      </div>
    </div>
  );
}

function ObservationGridForm({
  onSubmit,
  observationGrid,
  restoreItem,
  isFromImport = false,
}: any) {
  const { t } = useTranslation();
  const { navigate } = useRouter();
  const [errorMessage, setErrorMessage] = useState("");
  const { park: currentPark } = usePark();
  const { user: currentUser } = useAuth();
  const canManageObservationGrid = observationGrid?.id
    ? (currentUser?.isAdmin ||
        currentUser?.isSuperadmin ||
        ((currentUser?.isManager || currentUser?.isObserver) &&
          currentUser?.administrationPermissions?.["observation-grid"]
            ?.update)) ??
      false
    : (currentUser?.isAdmin ||
        currentUser?.isSuperadmin ||
        ((currentUser?.isManager || currentUser?.isObserver) &&
          currentUser?.administrationPermissions?.["observation-grid"]
            ?.create)) ??
      false;
  const canDeleteObservationGrid =
    (currentUser?.isAdmin ||
      currentUser?.isSuperadmin ||
      ((currentUser?.isManager || currentUser?.isObserver) &&
        currentUser?.administrationPermissions?.["observation-grid"]
          ?.delete)) ??
    false;

  const initialValues = useMemo(
    () =>
      observationGrid?.id || isFromImport
        ? {
            ...observationGrid,
            park: observationGrid?.park?.id ?? currentPark?.id,
          }
        : {
            name: "",
            park: currentPark?.id,
          },
    [currentPark?.id, observationGrid, isFromImport]
  );

  const onSubmitWrapper = async (values: any) => {
    const res = await onSubmit(values);

    if (res.message) {
      setErrorMessage(res.message);
    }
  };

  const handleCancel = useCallback(() => {
    if (isFromImport) {
      navigate(-1);
    } else {
      navigate("/observation-grids", { replace: true });
    }
  }, [isFromImport, navigate]);

  return (
    <Form
      onSubmit={onSubmitWrapper}
      mutators={{
        ...arrayMutators,
        setFieldValue: ([field]: any, state: any, utils: any) => {
          utils.changeValue(state, field.field, () => field.value);
        },
      }}
      subscription={{ values: true, submitting: true, pristine: true }}
      initialValues={initialValues}
      render={({ handleSubmit, values, form }) => {
        return (
          <div className="mx-auto max-w-full py-4 px-4 text-slate-500 text-base">
            {/* Header */}
            <EditHeader
              title={t(
                observationGrid?.id
                  ? "editItem.observationGrid"
                  : "newItem.observationGrid"
              )}
              subtitle={values.name}
              icon="TableCellsIcon"
              errorMessage={errorMessage}
              HeaderButtonsEdit={
                <HeaderButtonsEdit
                  inHeader
                  onSubmit={canManageObservationGrid ? handleSubmit : undefined}
                  onCancel={handleCancel}
                  extraButtonsComponent={
                    values?.id &&
                    canDeleteObservationGrid && [
                      {
                        title: values?.isArchived
                          ? t("common.restore")
                          : t("common.archived"),
                        icon: values?.isArchived
                          ? "RiInboxUnarchiveLine"
                          : "RiInboxArchiveLine",
                        onClick: async () => {
                          await restoreItem(values);
                        },
                      },
                    ]
                  }
                />
              }
            />
            <form onSubmit={handleSubmit} noValidate>
              <FormScrollError />

              <div className="responsive-flex gap-2 mt-2">
                <div className="md:w-full mb-2 md:mb-0">
                  <div className="flex flex-col gap-2 w-full">
                    <div className="white-box">
                      <div className="responsive-flex gap-4">
                        <div className="flex flex-col w-full">
                          <FormControl
                            name="name"
                            title={t("forms.gridName")}
                            required
                            containerStyle="flex w-full mb-4"
                            editable={canManageObservationGrid}
                            className="text-input"
                          />
                          <FormControl
                            name="link"
                            title={t("forms.gridLink")}
                            containerStyle="flex w-full mb-4"
                            editable={canManageObservationGrid}
                            className="text-input"
                          />
                          <div className="flex flex-row gap-2 items-center">
                            <Icon
                              name="InformationCircleIcon"
                              className="w-6 h-6 text-ternary"
                            />
                            <p className="w-full sm:w-1/2 my-4 text-black">
                              {t("forms.unnecessarySection")}
                            </p>
                          </div>
                          <FieldArray name="sections">
                            {({ fields }: any) => (
                              <div>
                                <SortableList
                                  items={fields}
                                  onMove={fields.move}
                                  render={(rest) =>
                                    SectionItem(
                                      form,
                                      values,
                                      rest,
                                      canManageObservationGrid ?? false
                                    )
                                  }
                                />

                                {canManageObservationGrid && (
                                  <FormAddButton
                                    label={t("newItem.section")}
                                    onClick={() =>
                                      fields.push({
                                        id: Math.floor(Math.random() * 10000),
                                      })
                                    }
                                  />
                                )}
                              </div>
                            )}
                          </FieldArray>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <HeaderButtonsEdit
                onSubmit={handleSubmit}
                onCancel={() => navigate("/observation-grids")}
              />
            </form>
          </div>
        );
      }}
    />
  );
}
export default ObservationGridForm;
